import {faSparkles} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Text} from '@rdcomponents/text/text';
import {Alert, Button, Drawer, List, Tooltip} from 'antd';
import {useCallback, useEffect, useRef, useState} from 'react';

import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {useLicenses} from '../../../../provider/license/license-hook';
import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {
    AiChatMessageRoleEnum,
    ConcordanceAiPresetEnum,
} from '../../../../service/reivew-analysis/review-analysis-ai-chat/review-analysis-ai-message-type';
import {
    useReviewsAiChat,
    useReviewsAiChatEligibility,
} from '../../../../service/reviews/reviews-ai-chat/reviews-ai-chat';
import {FeaturesEnum} from '../../../../service/user/user-type';
import * as styles from '../../reviews-analysis/pages/concordance-page/ai-analysis/ai-analysis.scss';
import {
    aiAnalysisPresetsTranslationKeys,
    getReviewsAiAssistantName,
} from '../../reviews-analysis/pages/concordance-page/ai-analysis/ai-analysis-const';
import {AiChatInput} from '../../reviews-analysis/pages/concordance-page/ai-analysis/chat-input/ai-chat-input';
import {AiChatMessage} from '../../reviews-analysis/pages/concordance-page/ai-analysis/message/ai-chat-message';
import {ReviewsStateType} from '../reviews-state/reviews-state-type';

import {MAX_REVIEWS_PER_GPT_REQUEST, MIN_REVIEWS_FOR_GPT} from './reviews-rocket-gpt-const';

type PropsType = {
    reviewsState: ReviewsStateType;
};

// eslint-disable-next-line complexity, max-statements
export function ReviewsRocketGpt(props: PropsType): JSX.Element | null {
    const {reviewsState} = props;
    const [open, setOpen] = useState(false);
    const {domainName, isWhiteLabel} = useDomainConfig();
    const {licenses} = useLicenses();
    const {getLocalizedString} = useLocale();

    // Button is fully hidden without an active Reputation licence (same signal that gates
    // the Concordance AI assistant). Not just disabled.
    const hasReviewAnalyticsLicence = Boolean(licenses[FeaturesEnum.reviewAnalyticsFull]?.isActive);
    const aiName = getReviewsAiAssistantName(domainName, isWhiteLabel);

    const {data: eligibility} = useReviewsAiChatEligibility(reviewsState, hasReviewAnalyticsLicence);
    const reviewsCount = eligibility?.reviewsCount;

    const {messages, sendMessage, isTyping, answerText} = useReviewsAiChat(reviewsState);

    const [inputValue, setInputValue] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // eslint-disable-next-line func-style
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    const handleSend = useCallback(() => {
        if (isTyping || !inputValue.trim()) {
            return;
        }

        sendMessage({message: inputValue.trim()});
        setInputValue('');
    }, [inputValue, isTyping, sendMessage]);

    const handleSendPreset = useCallback(
        (preset: ConcordanceAiPresetEnum) => {
            if (isTyping) {
                return;
            }

            sendMessage({
                presetKey: preset,
                message: getLocalizedString(aiAnalysisPresetsTranslationKeys[preset]),
            });
        },
        [getLocalizedString, isTyping, sendMessage]
    );

    if (!hasReviewAnalyticsLicence) {
        return null;
    }

    const hasReviewsCount = typeof reviewsCount === 'number';
    const reviewsCountValue = reviewsCount ?? 0;
    const isTooFewReviews = hasReviewsCount && reviewsCountValue < MIN_REVIEWS_FOR_GPT;
    const isDisabled = !hasReviewsCount || isTooFewReviews;
    const analyzedCount = Math.min(reviewsCountValue, MAX_REVIEWS_PER_GPT_REQUEST);
    const isSampled = hasReviewsCount && reviewsCountValue > MAX_REVIEWS_PER_GPT_REQUEST;

    return (
        <>
            <Tooltip
                title={isTooFewReviews ? getLocalizedString('REVIEWS_ANALYSIS__AI_ANALYSIS__MIN_REVIEWS_TOOLTIP') : ''}
            >
                <span>
                    <Button
                        className={styles.AiAnalysis_openButton}
                        disabled={isDisabled}
                        icon={<FontAwesomeIcon icon={faSparkles} />}
                        onClick={() => setOpen(true)}
                        type="primary"
                    >
                        {aiName}
                    </Button>
                </span>
            </Tooltip>
            <Drawer
                className={styles.AiAnalysis_drawer}
                footer={
                    <AiChatInput
                        disabled={isTyping}
                        inputValue={inputValue}
                        onSend={handleSend}
                        setInputValue={setInputValue}
                    />
                }
                onClose={() => setOpen(false)}
                open={open}
                placement="right"
                title={
                    <div>
                        <div>{`✨ ${aiName}`}</div>
                        {hasReviewsCount && (
                            <Text block secondary>
                                {getLocalizedString('REVIEWS_ANALYSIS__AI_ANALYSIS__ANALYZING_COUNT', {
                                    count: String(analyzedCount),
                                })}
                            </Text>
                        )}
                    </div>
                }
                width={450}
            >
                {isSampled && (
                    <Alert
                        message={getLocalizedString('REVIEWS_ANALYSIS__AI_ANALYSIS__SAMPLING_NOTICE', {
                            count: String(reviewsCountValue),
                        })}
                        showIcon
                        type="info"
                    />
                )}
                <List
                    dataSource={messages}
                    renderItem={(item) => (
                        <>
                            <List.Item className={styles.AiAnalysis_messageWrapper}>
                                <AiChatMessage message={item.text} role={item.role} title={item.title} />
                            </List.Item>

                            {item.role === AiChatMessageRoleEnum.SYSTEM && (
                                <List.Item className={styles.AiAnalysis_messageWrapper}>
                                    <div className={styles.AiAnalysis_presetsContainer}>
                                        <Text
                                            block
                                            secondary
                                            stringKey="REVIEWS_ANALYSIS__AI_ANALYSIS__PRESETS_TITLE"
                                        />
                                        {Object.values(ConcordanceAiPresetEnum).map((preset) => (
                                            <Button
                                                className={styles.AiAnalysis_presetButton}
                                                key={preset}
                                                onClick={() => handleSendPreset(preset)}
                                                size="small"
                                            >
                                                <Locale stringKey={aiAnalysisPresetsTranslationKeys[preset]} />
                                            </Button>
                                        ))}
                                    </div>
                                </List.Item>
                            )}
                        </>
                    )}
                />

                {isTyping && (
                    <AiChatMessage
                        message={answerText || getLocalizedString('REVIEWS_ANALYSIS__AI_ANALYSIS__TYPING', {aiName})}
                        role={AiChatMessageRoleEnum.ASSISTANT}
                        typing
                    />
                )}

                <div ref={messagesEndRef} />
            </Drawer>
        </>
    );
}
